import { useCallback, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";

// Seamless looping background video: two stacked players cross-fade over the
// last CROSS seconds so the cut from end → start is never visible. Muted,
// autoplay, with a small play/pause toggle bottom-left.
const CROSS = 0.7;

export const LoopVideo = ({ src, sources = [], poster, className = "", label = "video" }) => {
  const refs = [useRef(null), useRef(null)];
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(true);
  const switching = useRef(false);

  const onTime = (idx) => () => {
    const v = refs[idx].current;
    if (!v || idx !== active || switching.current || !v.duration) return;
    if (v.currentTime >= v.duration - CROSS) {
      const next = refs[1 - idx].current;
      switching.current = true;
      next.currentTime = 0;
      next.play().catch(() => {});
      setActive(1 - idx);
    }
  };

  const onEnded = (idx) => () => {
    const v = refs[idx].current;
    if (v) { v.pause(); v.currentTime = 0; }
    switching.current = false;
  };

  const toggle = useCallback(() => {
    const cur = refs[active].current;
    const other = refs[1 - active].current;
    if (playing) {
      cur?.pause(); other?.pause();
    } else {
      cur?.play().catch(() => {});
      if (switching.current) other?.play().catch(() => {});
    }
    setPlaying((p) => !p);
  }, [active, playing]);

  return (
    <div className={`relative overflow-hidden bg-[#0C3B3E] ${className}`} data-testid="loop-video">
      {poster && <img src={poster} alt="" aria-hidden="true" draggable={false} className="absolute inset-0 w-full h-full object-cover" />}
      {[0, 1].map((i) => (
        <video
          key={i}
          ref={refs[i]}
          muted
          playsInline
          preload="auto"
          autoPlay={i === 0}
          onTimeUpdate={onTime(i)}
          onEnded={onEnded(i)}
          aria-label={i === 0 ? label : undefined}
          data-testid={`loop-video-${i}`}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-linear ${active === i ? "opacity-100 z-[1]" : "opacity-0 z-0"}`}
        >
          {src && <source src={src} type="video/mp4" />}
          {sources.map((s) => <source key={s.src} src={s.src} type={s.type} />)}
        </video>
      ))}
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? "Pause video" : "Play video"}
        aria-pressed={playing}
        data-testid="loop-video-toggle"
        className="absolute left-5 bottom-5 z-[2] w-9 h-9 rounded-full bg-white/20 backdrop-blur-md border border-white/40 text-white flex items-center justify-center hover:bg-white/35 transition-colors"
      >
        {playing ? <Pause size={13} strokeWidth={2} fill="currentColor" /> : <Play size={13} strokeWidth={2} fill="currentColor" className="ml-0.5" />}
      </button>
    </div>
  );
};

export default LoopVideo;
